import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import EcoLogoWhite from "../../../img/eco/logo-white.png";
import { clearEmployeeSession } from "../../../store/employeeAuthSlice";
import type { RootState } from "../../../store/store";
import { COLABORADOR_LOGIN } from "../routes";

import "../pages/Colaborador.css";

type Props = {
  children: React.ReactNode;
  /** Sobrescreve o nome do cabeçalho enquanto `/dp/me/employee` não respondeu. */
  displayName?: string | null;
};

/** Duas letras para o avatar: primeiro e último nome. */
export const initialsOf = (name?: string | null): string => {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return (first + last).toUpperCase();
};

/**
 * Moldura das telas autenticadas do portal.
 *
 * Deliberadamente SEM a `Topbar` do staff: o colaborador não navega pelo
 * sistema interno, e um menu com "Operações"/"Estoque" (que ele não pode abrir)
 * só produziria telas de erro. O cabeçalho em faixa verde-floresta
 * é o mesmo das telas do staff no redesign — é o mesmo produto.
 */
const EmployeeShell: React.FC<Props> = ({ children, displayName }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const nomeDaSessao = useSelector((s: RootState) => s.employeeAuth?.fullName);
  const nome = displayName || nomeDaSessao || "Colaborador";

  const sair = () => {
    dispatch(clearEmployeeSession());
    navigate(COLABORADOR_LOGIN(), { replace: true });
  };

  return (
    <div className="colabPage">
      <header className="colabHeader">
        <img className="colabHeader__logo" src={EcoLogoWhite} alt="Eco System Serviços" />

        <div className="colabHeader__info">
          <p className="colabHeader__hello">Portal do Colaborador</p>
          <p className="colabHeader__name">{nome}</p>
        </div>

        <div className="colabAvatar" aria-hidden="true">
          {initialsOf(nome)}
        </div>

        <button type="button" className="colabHeader__exit" onClick={sair} title="Sair" aria-label="Sair">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          <span>Sair</span>
        </button>
      </header>

      <main className="colabContent">{children}</main>
    </div>
  );
};

export default EmployeeShell;
